import { createContext } from 'react';
import type { BusinessRead, ProfileRead } from '../../types/api';

/**
 * `unknown` means the lookup could not run (backend down, table not migrated,
 * transient network error). Route gating treats `unknown` as pass-through so a
 * backend hiccup never locks a signed-in user out of the app.
 */
export type OnboardingStatus = 'loading' | 'complete' | 'incomplete' | 'unknown';

export interface AccountContextValue {
  /** Profile row, or null while loading / when the lookup failed. */
  profile: ProfileRead | null;
  /** The active brand. Null until onboarding completes. */
  business: BusinessRead | null;
  /** Every brand the user belongs to, oldest first. Empty until onboarding completes. */
  businesses: readonly BusinessRead[];
  /** Derived gate state for protected routes. */
  onboardingStatus: OnboardingStatus;
  /** True while the profile/business lookup is in flight. */
  isLoading: boolean;
  /** Message from the last failed lookup, for surfacing in-page (never blocking). */
  error: string | null;
  /** Re-reads profile and business from the backend. */
  refresh: () => void;
  /** Replaces the cached profile after a successful profile write. */
  setProfile: (profile: ProfileRead) => void;
  /**
   * Makes another of the user's brands the active one. Remembered per user on
   * this device. An id not in the list is ignored.
   */
  setActiveBusinessId: (businessId: string) => void;
  /** Appends a just-created brand to the list and makes it active. */
  addBusiness: (business: BusinessRead) => void;
  /** Replaces one brand in the list after a successful brand write. */
  setBusiness: (business: BusinessRead) => void;
  /**
   * Drops a deleted brand from the list. If it was active the oldest remaining
   * brand takes over; if it was the last, the account is re-read so the
   * onboarding gate sees the cleared flag.
   */
  removeBusiness: (businessId: string) => void;
}

/**
 * Account context lives in the onboarding feature because onboarding is what
 * creates the profile and the first brand, and the onboarding gate is its main
 * consumer. Knowledge base screens read the active `business_id` from here via
 * `useBusiness` rather than fetching it per screen; the brand switcher in the
 * shell is the only thing that changes which one that is.
 */
export const AccountContext = createContext<AccountContextValue | null>(null);
